import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, Info, Megaphone, Check } from 'lucide-react';
import TabBar from '../components/layout/TabBar';
import Nastenka from '../components/komunikace/nastenka/Nastenka';
import Zadosti from '../components/komunikace/zadosti/Zadosti';
import Ukoly from '../components/komunikace/ukoly/Ukoly';
import Podekovat from '../components/komunikace/Podekovat';
import ChatWindow from '../components/ChatWindow';
import KartaKolegy from '../components/KartaKolegy';
import ModulLayout, { PANEL_OD } from '../components/layout/ModulLayout';
import ModalShell from '../components/ui/ModalShell';
import { REZERVA_LISTY } from '../components/MobilniLista';
import { useTheme } from '../contexts/ThemeContext';
import { SKLO_SVETLE } from '../lib/uiTokens';
import SeznamKonverzaci from '../components/komunikace/SeznamKonverzaci';
import NovaKonverzace from '../components/komunikace/NovaKonverzace';
import NastaveniKonverzace from '../components/komunikace/NastaveniKonverzace';
import OKonverzaci from '../components/komunikace/OKonverzaci';
import PrehledDnes from '../components/komunikace/PrehledDnes';
import { SECTION_COLOR, SECTION_BG, hlavicky } from '../components/komunikace/komunikace';

/**
 * Modul **Komunikace** (id `zpravy`) — chat se zaměstnanci.
 *
 * Norma rozvržení (`standard-rozvrzeni.md` §1): seznam konverzací · chat jako
 * detail · třetí sloupec. Bez výběru je ve třetím sloupci „Dnes" (nepřečtené,
 * kdo je na směně), s výběrem „O konverzaci" (členové, sdílené soubory).
 * Na mobilu seznam přes celou obrazovku a chat přes portál nad hlavičkou
 * (kvůli klávesnici, viz `ChatWindow`); „O konverzaci" je tam modal ⓘ.
 *
 * Nová konverzace a nastavení jsou modaly (`ModalShell`), dřív vlastní
 * `fixed` vrstva a blok rozbalený uvnitř chatu.
 */
export default function Zpravy({ user, initialKonverzaceId, initialTab = null, navParams = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const [vejdeSePanel, setVejdeSePanel] = useState(() => window.innerWidth >= PANEL_OD);
    useEffect(() => {
        const h = () => { setIsMobile(window.innerWidth < 768); setVejdeSePanel(window.innerWidth >= PANEL_OD); };
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    const [konverzace, setKonverzace] = useState([]);
    const [nacteno, setNacteno] = useState(false);
    const [activeId, setActiveId] = useState(initialKonverzaceId || null);
    const [pohled, setPohled] = useState('vse');
    const [hledani, setHledani] = useState('');
    const [onlineIds, setOnlineIds] = useState(new Set());
    const [lide, setLide] = useState(null);
    const [karta, setKarta] = useState(null);
    const [novaOtevrena, setNovaOtevrena] = useState(false);
    const [nastaveniOtevreno, setNastaveniOtevreno] = useState(false);
    const [infoOtevreno, setInfoOtevreno] = useState(false);
    const [detail, setDetail] = useState(null);
    const [obnovaDetailu, setObnovaDetailu] = useState(0);
    const [podekovat, setPodekovat] = useState(null);     // kolega z karty
    const [tab, setTab] = useState(initialTab || (navParams?.oznameniId ? 'nastenka' : null));
    const [kPotvrzeni, setKPotvrzeni] = useState([]);
    const [pocetUkolu, setPocetUkolu] = useState(0);
    useEffect(() => {
        const nacti = () => fetch('/api/ukoly/moje').then(r => (r.ok ? r.json() : null))
            .then(d => d && setPocetUkolu((d.ukoly?.length || 0) + (d.skupina || 0))).catch(() => {});
        nacti();
        const i = setInterval(nacti, 60000);
        return () => clearInterval(i);
    }, [tab]);

    // Proklik z notifikace na oznámení / konverzaci přepne záložku i v otevřeném modulu
    useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
    useEffect(() => { if (initialKonverzaceId) setTab('zpravy'); }, [initialKonverzaceId]);

    // Oznámení, která mám potvrdit — karta nad seznamem konverzací
    const nactiKPotvrzeni = useCallback(() => {
        fetch('/api/nastenka/k-potvrzeni').then(r => (r.ok ? r.json() : [])).then(d => setKPotvrzeni(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);
    useEffect(() => { nactiKPotvrzeni(); }, [nactiKPotvrzeni, tab]);
    useEffect(() => {
        window.addEventListener('nastenka-zmena', nactiKPotvrzeni);
        return () => window.removeEventListener('nastenka-zmena', nactiKPotvrzeni);
    }, [nactiKPotvrzeni]);
    const [otevritOznameni, setOtevritOznameni] = useState(navParams?.oznameniId ?? null);
    useEffect(() => { if (navParams?.oznameniId) setOtevritOznameni(navParams.oznameniId); }, [navParams?.oznameniId]);

    // Proklik odjinud (notifikace, toast) přepne konverzaci i v otevřeném modulu
    useEffect(() => { if (initialKonverzaceId) setActiveId(initialKonverzaceId); }, [initialKonverzaceId]);
    // Slot „Zprávy" ve spodní liště vede vždy na seznam konverzací — i když je
    // otevřená konverzace nebo jiná záložka (Nástěnka, Úkoly…).
    useEffect(() => {
        const naSeznam = () => { setTab('zpravy'); setActiveId(null); };
        window.addEventListener('zpravy-seznam', naSeznam);
        return () => window.removeEventListener('zpravy-seznam', naSeznam);
    }, []);
    useEffect(() => { setDetail(null); setInfoOtevreno(false); setNastaveniOtevreno(false); }, [activeId]);

    const nactiKonverzace = useCallback(() => {
        fetch('/api/chat/konverzace').then(r => (r.ok ? r.json() : [])).then(data => {
            const seznam = Array.isArray(data) ? data : [];
            setKonverzace(seznam);
            setNacteno(true);
            const celkem = seznam.reduce((s, k) => s + (k.neprectenych || 0), 0);
            window.dispatchEvent(new CustomEvent('chat-badge-update', { detail: { count: celkem } }));
        }).catch(() => setNacteno(true));
    }, []);

    useEffect(() => { nactiKonverzace(); }, [nactiKonverzace]);
    useEffect(() => { const i = setInterval(nactiKonverzace, 15000); return () => clearInterval(i); }, [nactiKonverzace]);
    useEffect(() => {
        window.addEventListener('app-resumed', nactiKonverzace);
        return () => window.removeEventListener('app-resumed', nactiKonverzace);
    }, [nactiKonverzace]);

    useEffect(() => {
        const nacti = () => fetch('/api/chat/online').then(r => (r.ok ? r.json() : [])).then(ids => setOnlineIds(new Set(ids))).catch(() => {});
        nacti();
        const i = setInterval(nacti, 30000);
        return () => clearInterval(i);
    }, []);

    // Adresář — pro pohled Lidé i pro třetí sloupec (kdo je dnes na směně)
    useEffect(() => {
        fetch('/api/chat/lide').then(r => (r.ok ? r.json() : [])).then(d => setLide(Array.isArray(d) ? d : [])).catch(() => setLide([]));
    }, []);

    // Otevřená konverzace pro service worker a toasty (nepípat do otevřeného chatu)
    useEffect(() => {
        window.__aktivniKonverzaceId = activeId ?? null;
        navigator.serviceWorker?.controller?.postMessage({ type: 'SET_ACTIVE_CHAT', konverzace_id: activeId ?? null });
        return () => {
            window.__aktivniKonverzaceId = null;
            navigator.serviceWorker?.controller?.postMessage({ type: 'SET_ACTIVE_CHAT', konverzace_id: null });
        };
    }, [activeId]);

    // Z karty kolegy nebo adresáře rovnou do konverzace — existující, nebo nová
    const napsat = async (clovek) => {
        setKarta(null);
        const stavajici = konverzace.find(k => k.typ === 'soukromy' && k.ucastnici?.some(u => u.id === clovek.id));
        if (stavajici) { setActiveId(stavajici.id); return; }
        try {
            const res = await fetch('/api/chat/konverzace', {
                method: 'POST', headers: hlavicky(),
                body: JSON.stringify({ typ: 'soukromy', uzivatel_ids: [clovek.id] }),
            });
            if (!res.ok) return;
            const k = await res.json();
            if (k?.id) { nactiKonverzace(); setActiveId(k.id); }
        } catch { /* síť — uživatel zkusí znovu */ }
    };

    const aktivni = konverzace.find(k => k.id === activeId);
    const druhy = aktivni?.typ === 'soukromy' ? aktivni.ucastnici?.find(u => u.id !== user?.id) : null;
    const isOnline = druhy ? onlineIds.has(druhy.id) : false;

    // Karta kolegy potřebuje kontakt a směnu — z adresáře, jinak z účastníka
    const otevriKartu = (clovek) => setKarta((lide || []).find(c => c.id === clovek.id) || clovek);

    // ── Vzhled ──
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const inputStyle = {
        width: '100%', height: 32, borderRadius: 8, padding: '0 10px', outline: 'none',
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };

    const poZmeneNastaveni = () => { setObnovaDetailu(n => n + 1); nactiKonverzace(); };
    const poOdchodu = () => { setNastaveniOtevreno(false); setActiveId(null); nactiKonverzace(); };

    const chat = (naMobilu) => (
        <ChatWindow konverzaceId={activeId} user={user} isDark={isDark} isOnline={isOnline}
            onBack={naMobilu ? () => setActiveId(null) : undefined}
            onKonverzaceZmenena={nactiKonverzace}
            onDetail={setDetail}
            obnovaDetailu={obnovaDetailu}
            onNastaveni={() => setNastaveniOtevreno(true)}
            onInfo={naMobilu || !vejdeSePanel ? () => setInfoOtevreno(true) : undefined} />
    );

    const oKonverzaci = (kompaktni) => (
        <OKonverzaci konverzaceId={activeId} detail={detail} user={user} lide={lide} onlineIds={onlineIds} isDark={isDark}
            kompaktni={kompaktni} onKarta={otevriKartu}
            onNastaveni={() => { setInfoOtevreno(false); setNastaveniOtevreno(true); }} />
    );

    const seznam = (siroky) => (
        <SeznamKonverzaci
            user={user} isDark={isDark} isMobile={isMobile} siroky={siroky} mutedColor={mutedColor} inputStyle={inputStyle} cardStyle={cardStyle}
            konverzace={konverzace} onlineIds={onlineIds} activeId={activeId} onVyber={setActiveId}
            pohled={pohled} setPohled={setPohled} hledani={hledani} setHledani={setHledani}
            lide={lide} onKarta={otevriKartu} onNapsat={napsat} onNova={() => setNovaOtevrena(true)}
            rezervaDole={isMobile ? REZERVA_LISTY : null} nahore={kartaKPotvrzeni} />
    );

    const pocetNeprectenych = konverzace.reduce((s, k) => s + (k.neprectenych || 0), 0);

    const potvrditOznameni = async (o) => {
        const res = await fetch(`/api/nastenka/${o.id}/potvrdit`, { method: 'POST', headers: hlavicky(false) });
        if (res.ok) nactiKPotvrzeni();
    };

    // Karta „Přečetl jsem" nad konverzacemi — nejdůležitější nepotvrzené oznámení
    const kartaKPotvrzeni = kPotvrzeni.length > 0 ? (() => {
        const o = kPotvrzeni[0];
        const nahled = o.text.replace(/[#*_~>]/g, '').replace(/^- /gm, '').trim();
        return (
            <div style={{ margin: isMobile ? '10px 12px 6px' : '10px 12px', borderRadius: 12, border: `1px solid ${SECTION_COLOR}66`, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', padding: '12px 14px' }}>
                <button type="button" onClick={() => { setOtevritOznameni(o.id); setTab('nastenka'); }} className="flex items-center gap-2 w-full text-left"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <Megaphone size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                    <span className="flex-1 min-w-0 truncate font-bold" style={{ color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{o.nazev}</span>
                    {kPotvrzeni.length > 1 && <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>+{kPotvrzeni.length - 1}</span>}
                </button>
                <p className="text-[13px]" style={{ color: 'var(--text-secondary)', margin: '6px 0 10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {nahled}
                </p>
                <button type="button" onClick={() => potvrditOznameni(o)} className="w-full flex items-center justify-center gap-1.5 rounded-xl font-bold"
                    style={{ height: isMobile ? 44 : 36, background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer' }}>
                    <Check size={16} /> Přečetl jsem
                </button>
            </div>
        );
    })() : null;

    return (
        <div style={isMobile
            ? { position: 'fixed', top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 2 }
            : { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {!isMobile && (
                <div className="flex items-center gap-3 mb-4" style={{ flexShrink: 0 }}>
                    <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                        <MessageCircle size={20} style={{ color: SECTION_COLOR }} />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Komunikace</h1>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {nacteno ? `${konverzace.length} konverzací${pocetNeprectenych ? ` · ${pocetNeprectenych} nepřečtených` : ''}` : 'Načítám…'} · zprávy, kanály a nástěnka
                        </p>
                    </div>
                </div>
            )}
            <div style={{ flexShrink: 0 }}>
                <TabBar modul="zpravy" user={user} aktivni={tab} onZmena={setTab} barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile}
                    pocty={{ ukoly: pocetUkolu, nastenka: kPotvrzeni.length }} />
            </div>

            {tab === 'ukoly' ? (
                <div className="flex flex-col" style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16 }}>
                    <Ukoly isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                        initialId={navParams?.ukolId ?? null} rezervaDole={isMobile ? REZERVA_LISTY : null} />
                </div>
            ) : tab === 'zadosti' ? (
                <div className="flex flex-col" style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16 }}>
                    <Zadosti user={user} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                        initialKlic={navParams?.zadost ?? null} rezervaDole={isMobile ? REZERVA_LISTY : null} />
                </div>
            ) : tab === 'nastenka' ? (
                <div className="flex flex-col" style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16 }}>
                    <Nastenka user={user} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} inputStyle={inputStyle} mutedColor={mutedColor}
                        initialOznameniId={otevritOznameni} rezervaDole={isMobile ? REZERVA_LISTY : null} />
                </div>
            ) : isMobile ? seznam(false) : (
                <div className="flex flex-col" style={{ flex: 1, minHeight: 0, paddingTop: 16 }}>
                <ModulLayout
                    isMobile={false}
                    vybrano={!!activeId}
                    panelVzdy
                    barva={SECTION_COLOR}
                    isDark={isDark}
                    seznam={({ siroky }) => seznam(siroky)}
                    detail={() => (
                        <div className="rounded-xl overflow-hidden flex flex-col flex-1 min-h-0" style={cardStyle}>
                            {chat(false)}
                        </div>
                    )}
                    panel={() => (activeId ? (
                        <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>{oKonverzaci(false)}</div>
                    ) : (
                        <PrehledDnes konverzace={konverzace} lide={lide} user={user} onlineIds={onlineIds} isDark={isDark} cardStyle={cardStyle}
                            onVyber={setActiveId} onKarta={otevriKartu} />
                    ))}
                />
                </div>
            )}

            {/* Mobil: chat přes celou obrazovku nad hlavičkou aplikace — portál,
                vlastní plocha aplikace (záře `--zare-mobil`), psací pole drží
                nad klávesnicí `ChatWindow`. */}
            {isMobile && activeId && (!tab || tab === 'zpravy') && createPortal(
                <div style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', flexDirection: 'column' }}>
                    <div aria-hidden="true" style={{
                        position: 'absolute', inset: 0, zIndex: 0,
                        backgroundColor: 'var(--surface-base)', backgroundImage: 'var(--zare-mobil)',
                        backgroundRepeat: 'no-repeat', backgroundSize: '100% 100%',
                    }} />
                    <div style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
                        {chat(true)}
                    </div>
                </div>,
                document.body
            )}

            {infoOtevreno && detail && (
                <ModalShell onClose={() => setInfoOtevreno(false)} isDark={isDark} nadpis="O konverzaci" ikona={Info} barva={SECTION_COLOR} sirka={440}>
                    {oKonverzaci(true)}
                </ModalShell>
            )}

            {nastaveniOtevreno && detail && (
                <NastaveniKonverzace konverzaceId={activeId} detail={detail} user={user} isDark={isDark}
                    onClose={() => setNastaveniOtevreno(false)} onZmena={poZmeneNastaveni} onOdejit={poOdchodu} />
            )}

            {novaOtevrena && (
                <NovaKonverzace user={user} isDark={isDark} onClose={() => setNovaOtevrena(false)}
                    onVytvoreno={(id) => { setNovaOtevrena(false); nactiKonverzace(); setActiveId(id); }} />
            )}

            {podekovat && (
                <Podekovat isDark={isDark} kolega={podekovat} onClose={() => setPodekovat(null)}
                    onHotovo={() => { setPodekovat(null); nactiKonverzace(); }} />
            )}

            {karta && (
                <KartaKolegy clovek={karta} onClose={() => setKarta(null)} onZprava={napsat}
                    onPodekovat={(c) => { setKarta(null); setPodekovat(c); }}
                    online={onlineIds.has(karta.id)} isDark={isDark} />
            )}
        </div>
    );
}
